"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useCurrentAccountId } from "@zapier/identity";
import { clearConnectHandoff, readConnectHandoff } from "@zapier/shared";
import type { ConnectionNeeded } from "@zapier/bff";

import { ConnectAppsModal } from "./ConnectAppsModal";

/**
 * How long the route must stay put before we open the modal. The post-copy
 * account switch lands on the target account and then redirects ONE more time
 * to the canonical/default folder (see `folders/[folder]/page.tsx` and the
 * folders-index → default-folder redirect). Opening on the first landing would
 * flash the modal, then re-open it after that redirect. Waiting for the route
 * to settle means we open only on the final page. Always opens eventually, so
 * there's no "never shows" risk.
 */
const ROUTE_SETTLE_MS = 500;

/**
 * Mounted once in the assets layout. After a cross-account copy redirects into
 * the target account, this reads the `sessionStorage` handoff left by
 * `CopyToAnotherAccountModal` and opens the "Connect your apps" modal once
 * we've landed in the account the assets were copied into AND the route has
 * settled (the post-copy redirect chain can navigate the destination one more
 * time — account-switch finalize, canonical/default-folder redirect).
 *
 * The handoff is cleared when the user dismisses the modal — NOT when it's
 * first read — so a transient remount during that redirect chain can't drop it.
 */
export function ConnectAppsModalHost() {
  const currentAccountId = useCurrentAccountId();
  const pathname = usePathname();
  const openedReference = useRef(false);
  const [connectionsNeeded, setConnectionsNeeded] = useState<
    ConnectionNeeded[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (openedReference.current) return;
    const handoff = readConnectHandoff();
    if (!handoff) return;

    // Only open once the current account is resolved AND matches the copy
    // target. This avoids opening on the source page and avoids a premature
    // flash during the account-switch settle (when the account is briefly
    // unresolved). The handoff stays in sessionStorage until then.
    if (String(currentAccountId ?? "") !== handoff.targetAccountId) {
      return;
    }

    if (handoff.connectionsNeeded.length === 0) {
      // Nothing to connect — consume it so we don't keep re-checking.
      clearConnectHandoff();
      return;
    }

    // Wait for the route to settle before opening. Any further navigation in
    // the post-copy redirect chain re-runs this effect (pathname changes) or
    // remounts the host (hard nav), and the cleanup cancels this pending open —
    // so the modal opens only once we've stopped redirecting. Prevents the
    // open → teardown → re-open flash.
    const timer = globalThis.setTimeout(() => {
      openedReference.current = true;
      setConnectionsNeeded(handoff.connectionsNeeded);
      setIsOpen(true);
    }, ROUTE_SETTLE_MS);
    return () => globalThis.clearTimeout(timer);
  }, [currentAccountId, pathname]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) clearConnectHandoff();
  }, []);

  if (!isOpen) return;

  return (
    <ConnectAppsModal
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      connectionsNeeded={connectionsNeeded}
    />
  );
}
